import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery, pyrotchBaseUrl } from "../axios/AxiosInstance";

export type IPRightType = "TRADEMARK" | "DESIGN_REGISTRATION" | "PATENT";

export interface RoleData {
  CD: number;
  ADMIN: number;
  CR: number;
  GENERAL: number;
}

export interface BulkDripRow {
  type: IPRightType;
  dripStatus?: "SUBMITTED" | "APPLIED" | "GRANTED" | "REJECTED" | "WITHDRAWN" | "PENDING" | "EDITED" | "APPROVED" | "DECLINED";
  role?: "CD" | "CR" | "ADMIN" | "GENERAL";
  projectName?: string;
  clientName?: string;
  description?: string;
  applicationNumber?: string;
  registrationNumber?: string;
  filingDate?: string;
  classification?: string;
  expiryDate?: string;
  grantDate?: string;
  actionRequired?: string;
  completeFilingDeadline?: string;
  internationalFilingDeadline?: string;
  examRequestDeadline?: string;
  designCode?: string;
  internalReference?: string;
  cbrNumber?: string;
  cbrDate?: string;
  legalTeam?: string;
  registrationFees?: number;
  legalFees?: number;
  expeditionFees?: number;
  invoiceNumber1?: string;
  invoiceDate1?: string;
  invoiceNumber2?: string;
  invoiceDate2?: string;
  imageFileName?: string;
  imageBase64?: string;
  imageUrl?: string;
}

export type RoleCounts = Record<IPRightType, RoleData>;

export const DRIPApi = createApi({
  baseQuery: axiosBaseQuery({
    baseUrl: pyrotchBaseUrl + "v1/",
  }),
  reducerPath: "DRIPApi",
  tagTypes: ["DRIP"],

  endpoints(build) {
    return {
      createDRIP: build.mutation<
        any,
        {
          id: number;
          projectId: number;
          type: string;
          projectName: string;
          role: string;
          description: string;
          applicationNumber?: string;
          registrationNumber?: string;
          filingDate?: string;
          classification?: string;
          expiryDate?: string;
          files?: File[];
        }
      >({
        query: ({ id, projectId, type, projectName, role, description, applicationNumber, registrationNumber, filingDate, classification, expiryDate, files }) => {
          const formData = new FormData();
          formData.append("id", String(id));
          formData.append("projectId", String(projectId));
          formData.append("type", type);
          formData.append("projectName", projectName);
          if (role) formData.append("role", role);
          formData.append("description", description);
          if (applicationNumber) formData.append("applicationNumber", applicationNumber);
          if (registrationNumber) formData.append("registrationNumber", registrationNumber);
          if (filingDate) formData.append("filingDate", filingDate);
          if (classification) formData.append("classification", classification);
          if (expiryDate) formData.append("expiryDate", expiryDate);
          (files || []).forEach((file) => formData.append("files", file));
          return {
            url: `drip`,
            method: "POST",
            data: formData,
          };
        },
        invalidatesTags: [{ type: "DRIP", id: "LIST" }],
      }),

      GetDRIp: build.query<
        any,
        { roles: any; search: string; page: number; size: number; type: string, startDate?: string, endDate?: string }
      >({
        query: ({ roles, search, page, size, type, startDate, endDate }) => ({
          url: `drip/all`,
          method: "POST",
          data: { roles, search, page, size, type, startDate, endDate },
        }),
        providesTags: [{ type: "DRIP", id: "LIST" }],
      }),

      GetDRIPCount: build.query<any, {startDate: string, endDate:string}>({
        query: ({startDate, endDate}) => ({
          url: `drip/counts?startDate=${startDate}&endDate=${endDate}`,
          method: "GET",
        }),
        providesTags: [{ type: "DRIP", id: "LIST" }],
      }),

      GrantDRIP: build.mutation<
        any,
        {
          dripId: number;
          grantDate: string;
          rejectDate: string;
        }
      >({
        query: ({ dripId, grantDate, rejectDate }) => ({
          url: `drip/grant/${dripId}`,
          method: "PUT",
          data: { grantDate, rejectDate },
        }),
        invalidatesTags: [{ type: "DRIP", id: "LIST" }],
      }),

      ApplyDRIP: build.mutation<
        any,
        { dripId: number; type: string; role: string; applyDate: string }
      >({
        query: ({ dripId, type, role, applyDate }) => ({
          url: `drip/apply/${dripId}`,
          method: "PUT",
          data: { type, role, applyDate },
        }),
        invalidatesTags: [{ type: "DRIP", id: "LIST" }],
      }),
      requestApproveDrip: build.mutation<
        any,
        { dripId: number; approved: boolean }
      >({
        query: ({ dripId, approved }) => ({
          url: `drip/${dripId}/approved`,
          method: "GET",
          params: { dripId, approved },
        }),
        invalidatesTags: [{ type: "DRIP", id: "LIST" }],
      }),
      bulkImportDRIP: build.mutation<
        { created: number; errors: string[] },
        BulkDripRow[]
      >({
        query: (rows) => ({
          url: `drip/bulk-import`,
          method: "POST",
          data: rows,
        }),
        invalidatesTags: [{ type: "DRIP", id: "LIST" }],
      }),
      deleteDRIP: build.mutation<any, { dripId: number }>({
        query: ({ dripId }) => ({
          url: `drip/${dripId}`,
          method: "DELETE",
        }),
        invalidatesTags: [{ type: "DRIP", id: "LIST" }],
      }),
      roleCount: build.query<RoleCounts, void>({
        query: () => ({
          url: "drip/role-counts",
          method: "GET",
        }),
        providesTags: [{ type: "DRIP", id: "LIST" }],
      }),
      statusLog: build.query<any, { dripId: number }>({
        query: ({ dripId }) => ({
          url: `drip/${dripId}/status-logs`,
          method: "GET",
        }),
        providesTags: [{ type: "DRIP", id: "LIST" }],
      }),
    };
  },
});

export const {
  useCreateDRIPMutation,
  useLazyGetDRIpQuery,
  useLazyGetDRIPCountQuery,
  useGrantDRIPMutation,
  useApplyDRIPMutation,
  useRequestApproveDripMutation,
  useBulkImportDRIPMutation,
  useDeleteDRIPMutation,
  useRoleCountQuery,
  useStatusLogQuery,
} = DRIPApi;
